/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { TClientData, TPageNotification } from '../../types';
import { TClientApprover } from '../../types/other';
import { executeHttpRequest } from '../../epics/api';
import { getErrorMessages } from '../../utils';

type TClientElectronicTimesheetApprovalProps = {
    clientId: string | number;
    clientData: TClientData;
    notification: TPageNotification;
    isClientView?: boolean;
};

export default function ClientElectronicTimesheetApproval({
    clientId,
    clientData,
    notification,
    isClientView = false,
}: TClientElectronicTimesheetApprovalProps) {
    const [error, setError] = useState<string | null>(null);
    const [approvers, setApprovers] = useState<TClientApprover[]>([]);
    const [isLoadingApprovers, setIsLoadingApprovers] = useState<boolean>(true);

    // Add approver
    const initialAddFields: Omit<TClientApprover, 'id'> = {
        first_name: '',
        surname: '',
        email: '',
    };
    const [addFields, setAddFields] = useState<Omit<TClientApprover, 'id'>>(initialAddFields);
    const [isAdding, setIsAdding] = useState<boolean>(false);

    // Edit approver
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editFields, setEditFields] = useState<Omit<TClientApprover, 'id'> | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Delete approver
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const onEditApproverClick = (approver: TClientApprover) => {
        setEditFields({
            first_name: approver.first_name,
            surname: approver.surname,
            email: approver.email,
        });
        setEditingId(approver.id);
        setError(null);
    };

    const clearAddFields = () => {
        setAddFields(initialAddFields);
    };

    const getStatusIcon = (value: boolean | null | undefined) => {
        if (value === true) return 'icon icon-check-12';
        if (value === false) return 'icon icon-close2';
        return '';
    };

    const isTempestAccountCorrect = (referralCode: string | null | undefined) => {
        if (!referralCode || referralCode.toLowerCase().startsWith('ref')) {
            return false;
        }
        return true;
    };

    const fetchApprovers = () => {
        setIsLoadingApprovers(true);
        executeHttpRequest(`clients/${clientId}/approvers/`)
            .then(response => {
                setIsLoadingApprovers(false);
                setApprovers(response.data.content || []);
            })
            .catch(err => {
                setIsLoadingApprovers(false);
                notification.error(
                    'Error',
                    `Could not fetch approvers: ${getErrorMessages(err)}`,
                );
            });
    };

    useEffect(() => {
        fetchApprovers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const parseErrorResponse = (err: any): string => {
        // Try to extract meaningful error messages from the response
        try {
            const errorDetail = err?.response?.data?.meta?.detail;

            // Check for approvers field errors in meta.detail.approvers (array of errors)
            if (errorDetail?.approvers && Array.isArray(errorDetail.approvers)) {
                const messages: string[] = [];
                errorDetail.approvers.forEach((approverError: any, index: number) => {
                    if (approverError && typeof approverError === 'object' && Object.keys(approverError).length > 0) {
                        Object.entries(approverError).forEach(([field, errors]) => {
                            if (Array.isArray(errors)) {
                                messages.push(`Approver ${index + 1} - ${field}: ${errors.join(', ')}`);
                            }
                        });
                    }
                });
                if (messages.length > 0) return messages.join('; ');
            }

            // Check for direct field errors in meta.detail (e.g., single approver POST/PATCH)
            if (errorDetail && typeof errorDetail === 'object') {
                const messages: string[] = [];
                Object.entries(errorDetail).forEach(([field, errors]) => {
                    if (Array.isArray(errors)) {
                        const fieldLabel = field.replace(/_/g, ' ');
                        messages.push(`${fieldLabel}: ${errors.join(', ')}`);
                    }
                });
                if (messages.length > 0) return messages.join('; ');
            }
        } catch (e) {
            // Fall through to default error handler
        }

        // Fallback to the utility function
        return getErrorMessages(err);
    };

    const handleAddApprover = () => {
        setIsAdding(true);
        setError(null);

        const payload = {
            email: addFields.email,
            first_name: addFields.first_name,
            surname: addFields.surname,
        };

        executeHttpRequest(`clients/${clientId}/approvers/`, 'post', payload)
            .then(() => {
                setIsAdding(false);
                clearAddFields();
                fetchApprovers();
                notification.success('Success', 'Approver added successfully');
            })
            .catch(err => {
                setIsAdding(false);
                setError(parseErrorResponse(err));
            });
    };

    const handleEditApprover = (approverId: number) => {
        setIsEditing(true);
        setError(null);

        const payload = {
            email: editFields.email,
            first_name: editFields.first_name,
            surname: editFields.surname,
        };

        executeHttpRequest(`clients/${clientId}/approvers/${approverId}/`, 'patch', payload)
            .then(() => {
                setIsEditing(false);
                setEditingId(null);
                fetchApprovers();
                notification.success('Success', 'Approver updated successfully');
            })
            .catch(err => {
                setIsEditing(false);
                setError(parseErrorResponse(err));
            });
    };

    const handleDeleteApprover = () => {
        setIsDeleting(true);
        setError(null);

        executeHttpRequest(`clients/${clientId}/approvers/${deletingId}/`, 'delete')
            .then(() => {
                setIsDeleting(false);
                setDeletingId(null);
                fetchApprovers();
                notification.success('Success', 'Approver deleted successfully');
            })
            .catch(err => {
                setIsDeleting(false);
                setError(parseErrorResponse(err));
            });
    };

    const renderActionButtons = (approver: TClientApprover) =>
        editingId === approver.id
            ? (
                <td className="actions center">
                    <button
                        disabled={isEditing}
                        onClick={() => handleEditApprover(approver.id)}
                        type="button"
                        aria-label="Save changes"
                    >
                        <i className="icon icon-check-12" />
                    </button>
                    <button
                        disabled={isEditing}
                        onClick={() => {
                            setEditingId(null);
                            setError(null);
                        }}
                        type="button"
                        aria-label="Cancel editing"
                    >
                        <i className="icon icon-close2" />
                    </button>
                </td>
            ) : (
                <td className="actions center">
                    <button
                        disabled={editingId && editingId !== approver.id}
                        onClick={() => onEditApproverClick(approver)}
                        type="button"
                        aria-label={`Edit ${approver.first_name} ${approver.surname}`}
                    >
                        <i className="icon icon-pencil-3" />
                    </button>
                    <button
                        disabled={editingId && editingId !== approver.id}
                        onClick={() => {
                            setDeletingId(approver.id);
                            setError(null);
                        }}
                        type="button"
                        aria-label={`Delete ${approver.first_name} ${approver.surname}`}
                    >
                        <i className="icon icon-bin-1" />
                    </button>
                </td>
        );

    const renderInputField = (
        fieldName: keyof Omit<TClientApprover, 'id'>,
        fields: Omit<TClientApprover, 'id'>,
        setFields: (approver: Omit<TClientApprover, 'id'>) => void,
        placeholder = '',
    ) => (
        <input
            style={{ minWidth: '100%' }}
            name={fieldName as string}
            type="text"
            maxLength={255}
            value={fields[fieldName]}
            onChange={event => {
                setFields({
                    ...fields,
                    [fieldName]: event.target.value,
                });
            }}
            placeholder={placeholder}
        />
    );

    const renderRows = () => approvers.map((approver: TClientApprover) => (
        <tr key={approver.id}>
            {editingId === approver.id && !isClientView
                ? (
                    <>
                        <td>{renderInputField('first_name', editFields, setEditFields)}</td>
                        <td>{renderInputField('surname', editFields, setEditFields)}</td>
                        <td>{renderInputField('email', editFields, setEditFields)}</td>
                    </>
                ) : (
                    <>
                        <td>{approver.first_name}</td>
                        <td>{approver.surname}</td>
                        <td>{approver.email}</td>
                    </>
                )
            }
            {!isClientView && renderActionButtons(approver)}
        </tr>
    ));

    const renderAddApproverRow = () => (
        <tr>
            <td>{renderInputField('first_name', addFields, setAddFields, 'First Name')}</td>
            <td>{renderInputField('surname', addFields, setAddFields, 'Surname')}</td>
            <td>{renderInputField('email', addFields, setAddFields, 'Email')}</td>
            <td className="actions center">
                <button
                    disabled={isAdding || !addFields.first_name || !addFields.surname || !addFields.email}
                    onClick={handleAddApprover}
                    type="button"
                    aria-label="Add approver"
                >
                    <i className="icon icon-add-12" />
                </button>
                <button
                    disabled={isAdding || (!addFields.first_name && !addFields.surname && !addFields.email)}
                    onClick={clearAddFields}
                    type="button"
                    aria-label="Clear fields"
                >
                    <i className="icon icon-close2" />
                </button>
            </td>
        </tr>
    );

    // Handle delete confirmation when deletingId changes
    useEffect(() => {
        if (deletingId && !isDeleting) {
            if (window.confirm('Are you sure you want to delete this approver?')) {
                handleDeleteApprover();
            } else {
                setDeletingId(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletingId]);

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">Electronic Timesheet Approval</div>
            </div>
            <div className="freelancer-details-section__content">
                <table className="table-default">
                    <tbody>
                        {!isClientView && (
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    <span>Tempest account number</span>
                                </td>
                                <td>
                                    <>
                                        <i className={getStatusIcon(isTempestAccountCorrect(clientData.referral_code))} />
                                        {!isTempestAccountCorrect(clientData.referral_code) && (
                                            <span style={{ marginLeft: '10px', color: '#856404' }}>
                                                Please contact Credit Control to resolve:{' '}
                                                <a href="mailto:sales.ledger@berryrecruitment.co.uk">sales.ledger@berryrecruitment.co.uk</a>
                                            </span>
                                        )}
                                    </>
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td className="table-default__cell--text-blue">
                                <span>Electronic Authorisation</span>
                            </td>
                            <td>
                                <i className={getStatusIcon(clientData.electronic_authorisation)} />
                            </td>
                        </tr>
                        <tr>
                            <td className="table-default__cell--text-blue">
                                <span>On Stop</span>
                            </td>
                            <td>
                                <i className={getStatusIcon(clientData.on_stop)} />
                                {!isClientView && clientData.on_stop && (
                                    <span style={{ marginLeft: '10px', color: '#856404' }}>
                                        This client is currently on stop. Please contact Credit Control to resolve:{' '}
                                        <a href="mailto:sales.ledger@berryrecruitment.co.uk">sales.ledger@berryrecruitment.co.uk</a>
                                    </span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__subtitle">Approvers</div>
            </div>
            <div className="freelancer-details-section__content">
                {!!error && (
                    <div className="freelancer-details-section__errors">
                        <p>{error}</p>
                    </div>
                )}
                {isLoadingApprovers ? (
                    <div className="fetching-freelancer-info">
                        <i className="icon icon-loading-1" />
                    </div>
                ) : (
                    <>
                        {isClientView && approvers.length === 0 ? (
                            <div className="freelancer-details-section__info">
                                <p>No approvers have been set up for this client yet.</p>
                            </div>
                        ) : (
                            <table className="table-default table-layout-fixed table-default__no-fixed-first-child-width">
                                <tbody>
                                    <tr>
                                        <td className="table-default__cell--text-blue">First Name</td>
                                        <td className="table-default__cell--text-blue">Surname</td>
                                        <td className="table-default__cell--text-blue">Email</td>
                                        {!isClientView && (
                                            <td className="table-default__cell--text-blue center table-default__cell--action">
                                                Action
                                            </td>
                                        )}
                                    </tr>
                                    {renderRows()}
                                    {!isClientView && renderAddApproverRow()}
                                </tbody>
                            </table>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

